import {
  AutocompleteRenderGroupParams,
  AutocompleteRenderInputParams,
  FilterOptionsState,
  SxProps,
  TextField,
  Theme,
} from '@mui/material'
import debounce from 'lodash/debounce'
import { useEffect, useRef, useState } from 'react'
import { BaseAutocomplete } from '../base-autocomplete/BaseAutocomplete'

const SCROLL_THRESHOLD = 100

const handleScroll = (event: React.UIEvent<HTMLUListElement>, hasMore: boolean, loadMore: () => void) => {
  const { scrollTop, scrollHeight, clientHeight } = event.currentTarget
  if (scrollHeight - scrollTop <= clientHeight + SCROLL_THRESHOLD && hasMore) {
    loadMore()
  }
}

type PaginationParams = {
  loadMore: () => void
  hasMore: boolean
  isLoading: boolean
  setSearchTerm: (searchTerm: string | undefined) => void
}

const DEFAULT_PAGINATION_PARAMS: PaginationParams = {
  loadMore: () => {},
  hasMore: false,
  isLoading: false,
  setSearchTerm: () => {},
}

export type SearchableDropdownProps<T> = {
  value: T | null
  options: T[]
  onChange: (value: T | null) => void
  getOptionLabel: (option: T) => string
  isOptionEqualToValue?: (option: T, value: T) => boolean
  renderOption?: (props: React.HTMLAttributes<HTMLLIElement> & { key: React.Key }, option: T) => React.ReactNode
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode
  filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[]
  groupBy?: (option: T) => string
  renderGroup?: (params: AutocompleteRenderGroupParams) => React.ReactNode
  getOptionDisabled?: (option: T) => boolean
  onBlur?: () => void
  disabled?: boolean
  hasError?: boolean
  placeholder?: string
  disableClearable?: boolean
  autoSelect?: boolean
  autoHighlight?: boolean
  openOnFocus?: boolean
  paginationParams?: PaginationParams
  forcePopupIcon?: boolean
  sx?: SxProps<Theme>
  'data-testid'?: string
}

export const SearchableDropdown = <T,>({
  value,
  options,
  onChange,
  getOptionLabel,
  isOptionEqualToValue,
  renderOption,
  renderInput,
  filterOptions,
  groupBy,
  renderGroup,
  getOptionDisabled,
  onBlur,
  disabled,
  hasError = false,
  placeholder,
  disableClearable = false,
  autoSelect = false,
  autoHighlight = true,
  openOnFocus = false,
  paginationParams,
  forcePopupIcon,
  sx,
  ...rest
}: SearchableDropdownProps<T>) => {
  const { loadMore, hasMore, isLoading, setSearchTerm } = paginationParams ?? DEFAULT_PAGINATION_PARAMS

  const [textFieldValue, setTextFieldValue] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const debouncedSearchRef = useRef(
    debounce((term: string, updateSearchTerm: (term: string | undefined) => void) => {
      updateSearchTerm(term)
      setIsTyping(false)
    }, 300)
  )

  useEffect(() => {
    const debouncedFn = debouncedSearchRef.current
    return () => debouncedFn.cancel()
  }, [])

  const handleInputChange = (_: unknown, newInputValue: string, reason: string) => {
    // Only update search text on user input, not on MUI 'reset' (value sync) or 'clear'
    if (reason !== 'input') return

    setTextFieldValue(newInputValue)
    if (!paginationParams) return

    if (newInputValue === '') {
      debouncedSearchRef.current.cancel()
      setIsTyping(false)
      paginationParams.setSearchTerm(undefined)
    } else {
      setIsTyping(true)
      debouncedSearchRef.current(newInputValue, paginationParams.setSearchTerm)
    }
  }

  const clearSearchState = () => {
    setTextFieldValue('')
    setIsTyping(false)
    debouncedSearchRef.current.cancel()
    setSearchTerm?.(undefined)
  }

  const defaultRenderInput = (params: AutocompleteRenderInputParams) => (
    <TextField {...params} placeholder={placeholder} error={hasError} />
  )

  return (
    <BaseAutocomplete<T, false, false, false>
      data-testid={rest['data-testid']}
      value={value ?? null}
      sx={sx}
      autoHighlight={autoHighlight}
      autoSelect={autoSelect}
      openOnFocus={openOnFocus}
      disableClearable={disableClearable as false}
      forcePopupIcon={forcePopupIcon}
      onOpen={() => setIsOpen(true)}
      onClose={() => {
        setIsOpen(false)
        clearSearchState()
      }}
      onChange={(_, selectedValue) => {
        onChange(selectedValue)
        clearSearchState()
        if (!selectedValue) onBlur?.()
      }}
      onBlur={onBlur}
      disabled={disabled}
      multiple={false}
      options={options}
      loading={isLoading || isTyping}
      getOptionLabel={(option) => (option ? getOptionLabel(option) : '')}
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={renderOption}
      filterOptions={filterOptions}
      groupBy={groupBy}
      renderGroup={renderGroup}
      getOptionDisabled={getOptionDisabled}
      slotProps={{
        listbox: {
          onScroll: (event) => handleScroll(event, hasMore, loadMore),
          style: { maxHeight: 300 },
        },
      }}
      inputValue={value !== null && !isOpen ? getOptionLabel(value) : textFieldValue}
      onInputChange={handleInputChange}
      renderInput={renderInput ?? defaultRenderInput}
    />
  )
}
